/**
 * The content library (brief section 7.4): Vimeo or YouTube videos added
 * by hand from their link (no sync with the Vimeo library), and PDFs.
 * Products are WR-1..WR-4 only; thumbnails are always generated (from the
 * video provider, or the first page of the PDF), never uploaded.
 */

export const ASSET_KINDS = {
  video: 'Video',
  brochure: 'Brochure',
  spec: 'Spec sheet',
  case: 'Case study',
  deck: 'Presentation',
  other: 'Other',
} as const;
export type AssetKind = keyof typeof ASSET_KINDS;
export const PDF_KINDS = ['brochure', 'spec', 'case', 'deck', 'other'] as const;

/** A PDF of the library: 50 MB at most (prototype). */
export const ASSET_PDF_MAX_BYTES = 50 * 1024 * 1024;

export type VideoProvider = 'youtube' | 'vimeo';

export interface VideoLink {
  provider: VideoProvider;
  id: string;
  /** Vimeo private links: the code after the number. */
  hash: string | null;
}

/** A YouTube or Vimeo link (watch, short, embed, live, unlisted or private), or null. */
export function parseVideoUrl(input: string): VideoLink | null {
  let url: URL;
  try {
    url = new URL(String(input).trim());
  } catch {
    return null;
  }
  if (url.protocol !== 'https:' && url.protocol !== 'http:') return null;
  const host = url.hostname.toLowerCase().replace(/^(www|m)\./, '');
  const youtube = (id: string | null | undefined) =>
    id && /^[\w-]{6,20}$/.test(id) ? { provider: 'youtube' as const, id, hash: null } : null;
  if (host === 'youtu.be') return youtube(url.pathname.slice(1).split('/')[0]);
  if (host === 'youtube.com' || host === 'youtube-nocookie.com') {
    if (url.searchParams.get('v')) return youtube(url.searchParams.get('v'));
    const m = /^\/(shorts|embed|live)\/([\w-]+)/.exec(url.pathname);
    return m ? youtube(m[2]) : null;
  }
  if (host === 'vimeo.com' || host === 'player.vimeo.com') {
    const m = /(?:^|\/)(\d{6,12})(?:\/([0-9a-f]{6,20}))?(?:\/|$)/i.exec(url.pathname);
    if (!m) return null;
    const hash = m[2] ?? url.searchParams.get('h');
    return {
      provider: 'vimeo',
      id: m[1]!,
      hash: hash && /^[0-9a-f]{6,20}$/i.test(hash) ? hash.toLowerCase() : null,
    };
  }
  return null;
}

/** The provider's player, without tracking where the provider allows it. */
export function embedUrl(v: { provider: VideoProvider; id: string; hash?: string | null }): string {
  if (v.provider === 'youtube') {
    return `https://www.youtube-nocookie.com/embed/${encodeURIComponent(v.id)}?rel=0&autoplay=1`;
  }
  const h = v.hash ? `h=${encodeURIComponent(v.hash)}&` : '';
  return `https://player.vimeo.com/video/${encodeURIComponent(v.id)}?${h}dnt=1&autoplay=1`;
}

/** The link to open the video on its provider's site. */
export function watchUrl(v: { provider: VideoProvider; id: string; hash?: string | null }): string {
  return v.provider === 'youtube'
    ? `https://www.youtube.com/watch?v=${encodeURIComponent(v.id)}`
    : `https://vimeo.com/${v.id}${v.hash ? `/${v.hash}` : ''}`;
}

export const providerName = (p: unknown) =>
  p === 'youtube' ? 'YouTube' : p === 'vimeo' ? 'Vimeo' : 'Link';

/** "#Product overview" to "product overview": lowercase, no #, 30 characters at most. */
export const normTag = (t: unknown) =>
  String(t ?? '')
    .trim()
    .toLowerCase()
    .replace(/^#+/, '')
    .replace(/\s+/g, ' ')
    .slice(0, 30);

/** "3.2 MB", "859 KB". */
export function fileSize(bytes: unknown): string {
  const n = Number(bytes);
  if (!Number.isFinite(n) || n <= 0) return '';
  if (n < 1024 * 1024) return `${Math.max(1, Math.round(n / 1024))} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

/** "Vimeo video, English" or "Brochure, English, 3.2 MB" (prototype). */
export function assetMeta(asset: object, languages: Readonly<Record<string, string>>): string {
  const a = asset as Readonly<Record<string, unknown>>;
  const kind =
    a.format === 'video'
      ? `${providerName(a.provider)} video`
      : (ASSET_KINDS[a.kind as AssetKind] ?? 'PDF');
  return [kind, languages[String(a.lang)] ?? '', fileSize(a.size_bytes)].filter(Boolean).join(', ');
}

/** Every tag of the library with its count, most used first. */
export function tagCounts(assets: readonly object[]): [string, number][] {
  const counts = new Map<string, number>();
  for (const a of assets as readonly Readonly<Record<string, unknown>>[]) {
    for (const t of (a.tags as string[] | null) ?? []) counts.set(t, (counts.get(t) ?? 0) + 1);
  }
  return [...counts].sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]));
}

export interface AssetFilter {
  q: string;
  kind: string;
  lang: string;
  product: string;
  tags: string[];
}

/** The library's search and filters (prototype): newest first. */
export function filterAssets<T extends object>(assets: readonly T[], f: AssetFilter): T[] {
  const q = f.q.trim().toLowerCase().replace(/^#/, '');
  const field = (a: T) => a as Readonly<Record<string, unknown>>;
  return [...assets]
    .sort((x, y) =>
      String(field(y).created_at ?? '').localeCompare(String(field(x).created_at ?? '')),
    )
    .filter((item) => {
      const a = field(item);
      const tags = (a.tags as string[] | null) ?? [];
      const products = (a.products as string[] | null) ?? [];
      return (
        (!f.kind || a.kind === f.kind) &&
        (!f.lang || a.lang === f.lang) &&
        (!f.product || products.includes(f.product)) &&
        f.tags.every((t) => tags.includes(t)) &&
        (!q ||
          [a.title, a.description, a.file_name, ...products, ...tags]
            .join(' ')
            .toLowerCase()
            .includes(q))
      );
    });
}

/* ---------- Offline copies of the videos (round 2) ---------- */

/** An MP4 a content admin adds to a video, played on the device without network: 1 GB at most. */
export const OFFLINE_VIDEO_MAX_BYTES = 1024 ** 3;
/** Sent straight to Spaces in parts of 16 MB; a cut connection resumes at the last part. */
export const OFFLINE_PART_BYTES = 16 * 1024 ** 2;
/** Shown where the copy is added (decision of Oct 30, 2026: 720p recommended). */
export const OFFLINE_VIDEO_ADVICE =
  'Export it in 720p (1280 × 720) from YouTube Studio: sharp on a phone or a tablet, and about half the size of 1080p. MP4 only, 1 GB at most.';

/** An MP4 (ISO base media file): its first box is "ftyp", whatever the name says. */
export function isMp4(head: Uint8Array): boolean {
  return head.length >= 8 && String.fromCharCode(...head.subarray(4, 8)) === 'ftyp';
}

/** A size for offline storage: "640 KB", "412 MB", "1.2 GB". */
export function storageSize(bytes: number): string {
  if (!Number.isFinite(bytes) || bytes <= 0) return '0 MB';
  if (bytes < 1024 ** 2) return `${Math.max(1, Math.round(bytes / 1024))} KB`;
  if (bytes < 1024 ** 3) return `${Math.round(bytes / 1024 ** 2)} MB`;
  return `${(bytes / 1024 ** 3).toFixed(1)} GB`;
}
